import { Button, Divider, Tooltip, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import RemoveShoppingCartIcon from '@material-ui/icons/RemoveShoppingCart';
import { AxiosError } from 'axios';
import CalculationsTable from 'components/CalculationsTable';
import DepictMolecule from 'components/DepictMolecule';
import {
  Cart,
  CartMember,
  getAvailabilityThunk,
  removeFromCart,
  SupplierItem,
} from 'features/shoppingCart/shoppingCartSlice';
import { useCalculations, useThunkDispatch } from 'hooks';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Calc } from 'services/FragnetService';

import CartSupplierResults from './CartSupplierResults';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
    },
    cartGroup: {
      display: 'grid',
      gridTemplateColumns: '1fr 9fr',
      gridGap: '8rem',
      padding: theme.spacing(2),
    },
    removeButton: {
      marginTop: theme.spacing(2),
    },
  }),
);

interface Props {
  cart: Cart;
  selectedSupplier: string | undefined;
  toggleSelectedCartMember: (cartMember: number) => (supplier: SupplierItem) => void;
  handleAvailabilityError: (error: AxiosError) => void;
}

export default ({
  cart,
  selectedSupplier,
  toggleSelectedCartMember,
  handleAvailabilityError,
}: Readonly<Props>) => {
  const { t } = useTranslation(['common', 'cart']);
  const classes = useStyles();
  const dispatch = useThunkDispatch();
  const calculations = useCalculations();

  const calcsLookup: { [key: string]: string } = {};
  calculations.forEach((calc: Calc) => {
    calcsLookup[calc.name] = calc.description;
  });

  const handleAvailability = (cartId: number, memberId: number, smiles: string) => {
    dispatch(getAvailabilityThunk(cartId, memberId, smiles)).catch(handleAvailabilityError);
  };

  return (
    <>
      {cart.items.length > 0 ? (
        <section className={classes.root}>
          {cart.items
            .filter(
              (member) =>
                !member.availability ||
                member.availability.filter(
                  (item) => !selectedSupplier || item.supplier === selectedSupplier,
                ).length,
            )
            .map((member: CartMember, index: number) => (
              <React.Fragment key={`${index}`}>
                <div className={classes.cartGroup} key={`${member.id}-${member.smiles}`}>
                  <div>
                    <DepictMolecule smiles={member.smiles} width={150} height={150} />
                    <CalculationsTable
                      groupMember={member}
                      calcs={calcsLookup}
                      blacklist={[
                        'neighbours',
                        'sim_morgan2_tanimoto',
                        'sim_rdkit_tanimoto',
                        'sim_morgan3_tanimoto',
                      ]}
                    />
                    <Tooltip arrow title={t('remove_from_cart')!}>
                      <Button
                        variant="outlined"
                        color="secondary"
                        className={classes.removeButton}
                        onClick={() => dispatch(removeFromCart(member.id))}
                      >
                        <RemoveShoppingCartIcon fontSize="small" />
                      </Button>
                    </Tooltip>
                  </div>
                  {member.availability && member.availability.length ? (
                    <CartSupplierResults
                      member={member}
                      availability={member.availability}
                      selectedSupplier={selectedSupplier}
                      toggleSelection={toggleSelectedCartMember(member.id)}
                    />
                  ) : (
                    <Button
                      onClick={() => handleAvailability(cart.cartId, member.id, member.smiles)}
                    >
                      {t('cart:fetch_availability')}
                    </Button>
                  )}
                </div>
                {index !== cart.items.length - 1 && <Divider />}
              </React.Fragment>
            ))}
        </section>
      ) : (
        <Typography>{t('cart:empty_is_cart')}</Typography>
      )}
    </>
  );
};
